import React, { useEffect, useState } from "react";
import { Receipt, ExternalLink, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const PROVIDER_LABEL = { stripe: "Card (Stripe)", paypal: "PayPal" };

function StatusBadge({ status }) {
  const paid = status === "paid";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
        paid ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
             : "bg-red-500/10 text-red-400 border border-red-500/30"
      }`}
      data-testid={`history-status-${status}`}
    >
      {paid ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {paid ? "Paid" : "Failed"}
    </span>
  );
}

export default function BillingHistory() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/billing/history")
      .then((r) => setRows(r.data?.payments || []))
      .catch(() => setRows([]));
  }, []);

  if (rows === null) {
    return (
      <div className="flex items-center gap-2 py-16 justify-center text-ink-muted" data-testid="billing-history-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading payment history…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-bg-surface p-10 text-center" data-testid="billing-history-empty">
        <Receipt className="h-10 w-10 mx-auto text-ink-muted mb-3" />
        <p className="text-sm text-ink-secondary">No payments yet.</p>
        <p className="text-xs text-ink-muted mt-1">Once you subscribe to a paid plan, every payment will show up here with its receipt.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="billing-history-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-widest text-ink-muted">
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Description</th>
            <th className="px-4 py-3">Method</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Receipt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.invoice_id || i} className="border-b border-line/50 last:border-0 hover:bg-bg-elevated/40 transition" data-testid={`history-row-${i}`}>
              <td className="px-4 py-3 whitespace-nowrap text-ink-secondary">
                {r.date ? new Date(r.date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
              </td>
              <td className="px-4 py-3 text-ink-primary">{r.description}</td>
              <td className="px-4 py-3 text-ink-secondary whitespace-nowrap">{PROVIDER_LABEL[r.provider] || r.provider}</td>
              <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                ${Number(r.amount || 0).toFixed(2)} <span className="text-ink-muted text-xs">{r.currency}</span>
              </td>
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3 text-right">
                {r.receipt_url ? (
                  <a href={r.receipt_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-brand-amber hover:text-brand-amber/80 text-xs font-medium transition"
                     data-testid={`history-receipt-link-${i}`}>
                    View <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-xs text-ink-muted font-mono">{r.receipt_number || "—"}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
